import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';

export const commissionKeys = {
  all: ['commissions'] as const,
  lists: () => [...commissionKeys.all, 'list'] as const,
  list: (params: api.CommissionListParams) => [...commissionKeys.lists(), params] as const,
  summaries: () => [...commissionKeys.all, 'summary'] as const,
  summary: (params: api.CommissionListParams) => [...commissionKeys.summaries(), params] as const,
};

export function useCommissions(params: api.CommissionListParams = {}) {
  return useQuery({
    queryKey: commissionKeys.list(params),
    queryFn: () => api.listCommissions(params),
  });
}

export function useCommissionSummary(params: api.CommissionListParams = {}) {
  return useQuery({
    queryKey: commissionKeys.summary(params),
    queryFn: () => api.getCommissionSummary(params),
  });
}

/** Invalidate every commission list + summary (status transitions touch totals). */
function invalidateCommissions(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: commissionKeys.all });
}

export function useMarkCommissionPaidOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.markCommissionPaidOut(publicId),
    onSuccess: () => {
      invalidateCommissions(qc);
      toast.success('Commission marked paid out');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useVoidCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.voidCommission(publicId),
    onSuccess: () => {
      invalidateCommissions(qc);
      toast.success('Commission voided');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.deleteCommission(publicId),
    onSuccess: () => {
      invalidateCommissions(qc);
      toast.success('Commission deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
